import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import {
  forwardRef,
  useState,
  useEffect,
  type ReactNode,
  type ChangeEvent,
  type FocusEvent,
  type ForwardedRef,
  type TextareaHTMLAttributes,
} from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, DefaultProps {
  label?: string
  error?: string
  icon?: ReactNode
  valueFormatter?: (_value: string) => string
  isPII?: boolean
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { label, error, className, testId, id, ariaLabel, disabled, icon, isPII, valueFormatter, ...props },
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [hasValue, setHasValue] = useState(Boolean(props.value))

    useEffect(() => {
      setHasValue(Boolean(props.value))
    }, [props.value])

    const textareaClasses = twMerge(
      cn(
        'border leading-6 px-4 rounded w-full disabled:text-gray-400 disabled:bg-gray-50',
        'typo-primary-base-regular text-text-body',
        {
          'pt-[28px] pb-[12px]': Boolean(label),
          'pt-[20px] pb-[20px]': !label,
          'border-gray-200 outline-none focus:border-gray-800 hover:border-gray-400 disabled:border-gray-300': !error,
          'border-red-700 pr-9 outline-none focus:ring-red-500 bg-red-50 disabled:border-gray-300': Boolean(error),
          'cursor-not-allowed touch-none': Boolean(disabled),
          'fs-exclude': isPII,
        },
      ),
      className,
    )

    const labelClasses = cn(
      'pointer-events-none absolute ml-4 text-text-meta transition-all duration-200 ease-in-out origin-top-left',
      {
        'translate-y-[22px]': !isFocused && !hasValue,
        'font-semibold scale-75 translate-y-[10px]': isFocused || hasValue,
        'text-gray-500': !isFocused,
        'text-gray-900': isFocused,
        'opacity-70': disabled,
      },
    )

    return (
      <div>
        {Boolean(error) && <p className="text-red-600 text-xs font-semibold">{error}</p>}
        <div className="relative">
          {label ? <span className={labelClasses}>{label}</span> : null}
          <textarea
            {...(testId ? { 'data-testid': testId } : {})}
            {...(id ? { id } : {})}
            {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
            className={textareaClasses}
            ref={ref}
            disabled={disabled}
            {...props}
            onFocus={(event: FocusEvent<HTMLTextAreaElement>) => {
              setIsFocused(true)
              props.onFocus && props.onFocus(event)
            }}
            onBlur={(event: FocusEvent<HTMLTextAreaElement>) => {
              setIsFocused(false)
              props.onBlur && props.onBlur(event)
            }}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              setHasValue(Boolean(event.target.value))
              props.onChange && props.onChange(event)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.stopPropagation()
                props.onKeyDown && props.onKeyDown(event)
              }
            }}
            onInput={(event) => {
              if (valueFormatter) {
                event.currentTarget.value = valueFormatter(event.currentTarget.value)
              }
              if (props.onInput) {
                props.onInput(event)
              }
            }}
          />
          {error ? (
            <span
              className={cn('absolute right-3 top-1/2 -translate-y-1/2 transform pointer-events-none', {
                'right-3': !icon,
                'right-9': Boolean(icon),
              })}
            >
              <InformationCircleIcon className="w-5 h-5 fill-red-400" />
            </span>
          ) : null}
          {icon ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 transform pointer-events-none">{icon}</span>
          ) : null}
        </div>
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'

export { Textarea }
